import React from 'react';
import { Card } from '../components/ui/Card';
import { formatCurrency } from '../utils/calculations';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
export function NetProfit() {
  const profitData = {
    revenue: [{
      name: 'Service Revenue',
      amount: 285000
    }, {
      name: 'Product Sales',
      amount: 125000
    }, {
      name: 'Consulting Fees',
      amount: 45000
    }, {
      name: 'Other Income',
      amount: 8000
    }],
    costOfGoodsSold: [{
      name: 'Materials',
      amount: 95000
    }, {
      name: 'Direct Labor',
      amount: 120000
    }, {
      name: 'Subcontractors',
      amount: 35000
    }],
    operatingExpenses: [{
      name: 'Salaries & Wages',
      amount: 85000
    }, {
      name: 'Rent',
      amount: 24000
    }, {
      name: 'Utilities',
      amount: 6000
    }, {
      name: 'Insurance',
      amount: 12000
    }, {
      name: 'Marketing & Advertising',
      amount: 15000
    }, {
      name: 'Office Supplies',
      amount: 4000
    }, {
      name: 'Professional Services',
      amount: 8000
    }, {
      name: 'Depreciation',
      amount: 10000
    }, {
      name: 'Other Expenses',
      amount: 7000
    }],
    otherIncomeExpenses: [{
      name: 'Interest Income',
      amount: 2000
    }, {
      name: 'Interest Expense',
      amount: -5000
    }]
  };
  const totalRevenue = profitData.revenue.reduce((sum, item) => sum + item.amount, 0);
  const totalCOGS = profitData.costOfGoodsSold.reduce((sum, item) => sum + item.amount, 0);
  const grossProfit = totalRevenue - totalCOGS;
  const grossMargin = grossProfit / totalRevenue * 100;
  const totalOperatingExpenses = profitData.operatingExpenses.reduce((sum, item) => sum + item.amount, 0);
  const operatingIncome = grossProfit - totalOperatingExpenses;
  const operatingMargin = operatingIncome / totalRevenue * 100;
  const totalOtherIncomeExpenses = profitData.otherIncomeExpenses.reduce((sum, item) => sum + item.amount, 0);
  const netIncome = operatingIncome + totalOtherIncomeExpenses;
  const netMargin = netIncome / totalRevenue * 100;
  return <div className="space-y-6" data-id="element-1588">
      <div data-id="element-1589">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1590">Net Profit</h1>
        <p className="text-sm text-gray-600" data-id="element-1591">
          Profit & Loss Statement for{' '}
          {new Date().toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric'
        })}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1592">
        <Card data-id="element-1593">
          <div className="flex items-center justify-between" data-id="element-1594">
            <div data-id="element-1595">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1596">Gross Profit</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1597">
                {formatCurrency(grossProfit)}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1598">
                {grossMargin.toFixed(1)}% margin
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1599">
              <TrendingUpIcon className="w-6 h-6 text-blue-600" data-id="element-1600" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1601">
          <div className="flex items-center justify-between" data-id="element-1602">
            <div data-id="element-1603">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1604">Operating Income</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1605">
                {formatCurrency(operatingIncome)}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1606">
                {operatingMargin.toFixed(1)}% margin
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-1607">
              <TrendingUpIcon className="w-6 h-6 text-purple-600" data-id="element-1608" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1609">
          <div className="flex items-center justify-between" data-id="element-1610">
            <div data-id="element-1611">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1612">Net Income</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-1613">
                {formatCurrency(netIncome)}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1614">
                {netMargin.toFixed(1)}% margin
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1615">
              <TrendingUpIcon className="w-6 h-6 text-green-600" data-id="element-1616" />
            </div>
          </div>
        </Card>
      </div>

      {/* Revenue */}
      <Card data-id="element-1617">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1618">Revenue</h2>
        <div className="space-y-2" data-id="element-1619">
          {profitData.revenue.map((item, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-1620">
              <span className="text-gray-700" data-id="element-1621">{item.name}</span>
              <span className="font-medium text-gray-900" data-id="element-1622">
                {formatCurrency(item.amount)}
              </span>
            </div>)}
          <div className="flex justify-between items-center py-3 font-bold bg-blue-50 px-3 rounded" data-id="element-1623">
            <span className="text-gray-900" data-id="element-1624">Total Revenue</span>
            <span className="text-blue-600" data-id="element-1625">
              {formatCurrency(totalRevenue)}
            </span>
          </div>
        </div>
      </Card>

      {/* Cost of Goods Sold */}
      <Card data-id="element-1626">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1627">
          Cost of Goods Sold
        </h2>
        <div className="space-y-2" data-id="element-1628">
          {profitData.costOfGoodsSold.map((item, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-1629">
              <span className="text-gray-700" data-id="element-1630">{item.name}</span>
              <span className="font-medium text-gray-900" data-id="element-1631">
                {formatCurrency(item.amount)}
              </span>
            </div>)}
          <div className="flex justify-between items-center py-3 font-bold bg-red-50 px-3 rounded" data-id="element-1632">
            <span className="text-gray-900" data-id="element-1633">Total COGS</span>
            <span className="text-red-600" data-id="element-1634">{formatCurrency(totalCOGS)}</span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t-2 border-gray-300" data-id="element-1635">
          <div className="flex justify-between items-center" data-id="element-1636">
            <span className="text-lg font-bold text-gray-900" data-id="element-1637">
              Gross Profit
            </span>
            <span className="text-lg font-bold text-blue-600" data-id="element-1638">
              {formatCurrency(grossProfit)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right" data-id="element-1639">
            {grossMargin.toFixed(1)}% margin
          </p>
        </div>
      </Card>

      {/* Operating Expenses */}
      <Card data-id="element-1640">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1641">
          Operating Expenses
        </h2>
        <div className="space-y-2" data-id="element-1642">
          {profitData.operatingExpenses.map((item, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-1643">
              <span className="text-gray-700" data-id="element-1644">{item.name}</span>
              <span className="font-medium text-gray-900" data-id="element-1645">
                {formatCurrency(item.amount)}
              </span>
            </div>)}
          <div className="flex justify-between items-center py-3 font-bold bg-orange-50 px-3 rounded" data-id="element-1646">
            <span className="text-gray-900" data-id="element-1647">Total Operating Expenses</span>
            <span className="text-orange-600" data-id="element-1648">
              {formatCurrency(totalOperatingExpenses)}
            </span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t-2 border-gray-300" data-id="element-1649">
          <div className="flex justify-between items-center" data-id="element-1650">
            <span className="text-lg font-bold text-gray-900" data-id="element-1651">
              Operating Income
            </span>
            <span className="text-lg font-bold text-purple-600" data-id="element-1652">
              {formatCurrency(operatingIncome)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right" data-id="element-1653">
            {operatingMargin.toFixed(1)}% margin
          </p>
        </div>
      </Card>

      {/* Other Income/Expenses */}
      <Card data-id="element-1654">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1655">
          Other Income & Expenses
        </h2>
        <div className="space-y-2" data-id="element-1656">
          {profitData.otherIncomeExpenses.map((item, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-1657">
              <span className="text-gray-700" data-id="element-1658">{item.name}</span>
              <span className={`font-medium ${item.amount >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1659">
                {formatCurrency(item.amount)}
              </span>
            </div>)}
          <div className="flex justify-between items-center py-3 font-bold bg-gray-50 px-3 rounded" data-id="element-1660">
            <span className="text-gray-900" data-id="element-1661">Total Other Income/Expenses</span>
            <span className={totalOtherIncomeExpenses >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-1662">
              {formatCurrency(totalOtherIncomeExpenses)}
            </span>
          </div>
        </div>
      </Card>

      {/* Net Income */}
      <Card className="bg-green-50" data-id="element-1663">
        <div className="flex justify-between items-center mb-2" data-id="element-1664">
          <span className="text-2xl font-bold text-gray-900" data-id="element-1665">Net Income</span>
          <span className="text-2xl font-bold text-green-600" data-id="element-1666">
            {formatCurrency(netIncome)}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm" data-id="element-1667">
          <span className="text-gray-600" data-id="element-1668">Net Profit Margin</span>
          <span className="font-semibold text-green-600" data-id="element-1669">
            {netMargin.toFixed(1)}%
          </span>
        </div>
      </Card>
    </div>;
}